import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FormControl } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'

import SearchIcon from '@mui/icons-material/Search'

import { Checkbox, FormControlLabel } from '../styles/Checkbox'
import { Divider, FlexColumn, FlexEndContainer, LightBGContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { BoldBodyText } from '../styles/Text'
import { TextField, SelectMenuItem } from '../styles/TextField'
import PIRTable from './PIRTable'
import Tooltip from '../common/Tooltip'
import { isPirPaymentEnabled } from '../../util/pirConsts'
import pirTerminology from '../../util/pirTerminology'

const FieldWrapper = styled(FormControl)`
  width: 225px;
`

const TableWrapper = styled(LightBGContainer)`
  flex-basis: 100%;
`

const FlexContainer = styled.div`
  display: flex;
  gap: ${props => (props.gap !== undefined ? props.gap : '15px')};
  align-items: center;
`

const Label = styled(FormControlLabel)`
  margin: -8px 0;

  .MuiTypography-root {
    font-size: 12px;
  }
`

function getStatusOptions() {
  const options = [
    { value: 'ready', label: 'Ready' },
    { value: 'signing', label: 'Signing' },
    { value: 'paid', label: 'Paid' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' }
  ]

  if (!isPirPaymentEnabled()) return options.filter(option => option.value !== 'paid')
  return options
}

function FilteredPIRs({ pirList }) {
  const [filteredPirs, setFilteredPirs] = useState(pirList)
  const [searchedFilteredPirs, setSearchedFilteredPirs] = useState()
  const [searchValue, setSearchValue] = useState('')
  const [displayRequestor, setDisplayRequestor] = useState(true)

  function searchPirs() {
    if (searchValue.trim() === '') {
      setSearchedFilteredPirs(filteredPirs)
    } else {
      const searchVal = searchValue.toLowerCase()
      const searched = filteredPirs.filter(
        p =>
          `${p.address} ${p.postalCode}`.toLowerCase().search(searchVal) >= 0 ||
          p.requestor.toLowerCase().search(searchVal) >= 0 ||
          p.pirId.toLowerCase().search(searchVal) >= 0
      )
      setSearchedFilteredPirs(searched)
    }
  }

  useEffect(() => {
    if (filteredPirs) searchPirs()
  }, [filteredPirs])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPirs()
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchValue, 300])

  function onFilterChange(e) {
    if (e.target.value !== 'none') {
      const filtered = pirList.filter(p => p.status.toLowerCase() === e.target.value)
      setFilteredPirs(filtered)
    } else {
      setFilteredPirs(pirList)
    }
  }

  const handleSearchChange = e => setSearchValue(e.target.value)
  const handleChangeRequestor = e => setDisplayRequestor(e.target.checked)

  return (
    <FlexColumn gap="15px" width="100%">
      <LightBGContainer padding="25px 30px">
        <SpaceBetweenRowContainer>
          <FlexContainer>
            <BoldBodyText id="selector-label">FILTER BY STATUS</BoldBodyText>
            <FieldWrapper>
              <TextField select aria-labelledby="selector-label" defaultValue="none" onChange={onFilterChange}>
                <SelectMenuItem value="none">None</SelectMenuItem>
                {getStatusOptions().map(({ value, label }) => (
                  <SelectMenuItem key={value} value={value}>
                    {label}
                  </SelectMenuItem>
                ))}
              </TextField>
            </FieldWrapper>
          </FlexContainer>
          <FlexContainer gap="10px">
            <TextField
              aria-label="Search"
              placeholder="Search..."
              onChange={handleSearchChange}
              value={searchValue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Tooltip
              info={
                `${pirTerminology.getRequestPluralName()} are searched on the ${pirTerminology.getRequestIdLabel()}, ` +
                'Address, and Requester Name fields.'
              }
            />
          </FlexContainer>
        </SpaceBetweenRowContainer>
      </LightBGContainer>
      <TableWrapper padding="20px 50px 40px">
        {searchedFilteredPirs && (
          <FlexColumn gap="15px">
            <FlexEndContainer>
              <Label control={<Checkbox checked={displayRequestor} onChange={handleChangeRequestor} />} label={`Show "Requestor Name" Column`} />
            </FlexEndContainer>
            <Divider />
            <PIRTable pirList={searchedFilteredPirs} displayRequestor={displayRequestor} />
          </FlexColumn>
        )}
      </TableWrapper>
    </FlexColumn>
  )
}

FilteredPIRs.propTypes = {
  pirList: PropTypes.array.isRequired
}

export default FilteredPIRs
